import { post } from '@citrite/http';
import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { environment } from 'Environment';
import { getDefaultLeasingProperties } from 'Environment/ChromeApp/ChromeAppTelemetryLaunch';
import { ListenToChromeAppMessageType } from 'Environment/ChromeApp/types';
import { launchResource } from 'Environment/launchResource';
import { isCitrixChromeApp } from 'Environment/launchResource/device';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { getLatestResourceStatus } from 'Environment/launchResource/launchUtil';
import { ChromeAppContext } from 'Workspace/ChromeApp/ChromeAppContext';
import { ResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { createMessageQueue } from './MessageQueue';

let chromeAppWindow: Window = null;
let chromeAppOrigin: string = null;
const fullDetails = 'Full';
let shieldEnabled = false;

const HeaderType = {
	LaunchStatus: 'launchStatus',
	Troubleshoot: 'troubleshoot',
} as const;

interface QueuedMessage {
	data: any;
	origin: string;
}

interface LaunchStatusPayload {
	data: LaunchStatusData;
	correlationId: string;
}

interface LaunchStatusData {
	status: 'success' | 'failure';
	resourceId: string;
	errorId?: string;
	errorMessage?: string;
}

export interface TroubleshootPayload {
	data: TroubleshootData;
	correlationId: string;
}

export interface TroubleshootData {
	resourceId: string;
	errorId: string;
	action: string;
	errorMessage: string;
}

const messageQueue = createMessageQueue<QueuedMessage>();

let troubleshootHandler: ((payload: TroubleshootPayload) => void) | null = null;

function handleTroubleshootMessage(payload: TroubleshootPayload) {
	troubleshootHandler?.(payload);
}

export function registerTroubleshootHandler(fn: typeof troubleshootHandler) {
	troubleshootHandler = fn;
}

export function initializeChromeApp() {
	return new Promise<void>(resolve => {
		if (isCitrixChromeApp()) {
			window.addEventListener(
				'message',
				event => {
					const { data, origin } = event;
					const messageIdentifier =
						data?.data || data?.messageType || data?.header?.type || data?.type;

					if (messageIdentifier) {
						postAcknowledgementToChromeApp(messageIdentifier, data?.correlationId);
					}

					if (data.data === 'offlineMode') {
						environment.setCitrixCloudConnectedStatus(false);
						return;
					}
					if (data.messageType === 'setCapabilities') {
						shieldEnabled = data.status === 'successful';
						return;
					}
					if (data.type === 'fetchIca') {
						if (!isChromeAppListenerReady()) {
							messageQueue.enqueue({ data, origin });
							return;
						}
						return;
					}
					if (
						[HeaderType.Troubleshoot, HeaderType.LaunchStatus].includes(
							event.data.header?.type
						)
					) {
						if (!isChromeAppListenerReady()) {
							messageQueue.enqueue({ data: event.data, origin: event.origin });
						}
						return;
					}
					if (data !== 'ICA') {
						return;
					}
					chromeAppWindow = event.source as Window;
					chromeAppOrigin = origin;
					postMessageToChromeApp({ type: 'HideAccountSettings' });
					resolve();
				},
				false
			);
		} else {
			resolve();
		}
	});
}

function isChromeAppListenerReady() {
	return messageQueue.isListenerReady();
}

export function shieldToggleFromChromeapp() {
	return shieldEnabled;
}

export function addChromeAppListener(
	getWorkspaceConfiguration: () => WorkspaceConfiguration,
	getResourceContext: () => ResourceContext,
	updateChromeAppContext: ChromeAppContext['updateChromeAppContext']
) {
	if (isCitrixChromeApp()) {
		window.addEventListener(
			'message',
			event => {
				const { data, origin } = event;
				const fromExpectedOrigin = chromeAppOrigin === origin;
				if (
					!fromExpectedOrigin ||
					!data ||
					(!data.type && !data.messageType && !data.header?.type)
				) {
					return;
				}
				const messageIdentifier = data?.header?.type || data?.type || data?.messageType;
				postAcknowledgementToChromeApp(messageIdentifier, data?.correlationId);
				receiveMessageFromChromeApp(
					data,
					getWorkspaceConfiguration(),
					getResourceContext(),
					updateChromeAppContext
				);
			},
			false
		);
		messageQueue.setReady();

		messageQueue.processAndClearAsync(queuedMessage => {
			const queuedMessageIdentifier =
				queuedMessage.data?.header?.type || queuedMessage.data?.type;
			if (queuedMessageIdentifier) {
				postAcknowledgementToChromeApp(
					queuedMessageIdentifier,
					queuedMessage.data?.correlationId
				);
			}
			receiveMessageFromChromeApp(
				queuedMessage.data,
				getWorkspaceConfiguration(),
				getResourceContext(),
				updateChromeAppContext
			);
		});
	}
}

export interface ChromeAppLaunchOptions {
	/** This will be set when WSUI is instructed by the Chrome App to launch a resource */
	uniqueIdentifier: string;
}

function launchResourceForChromeApp(
	workspaceConfiguration: WorkspaceConfiguration,
	resourceContext: ResourceContext,
	resourceId: string,
	correlationId: string
) {
	const resource = resourceContext.resources.find(r => r.id === resourceId);
	if (!resource) {
		return;
	}
	const chromeAppOptions: ChromeAppLaunchOptions = {
		uniqueIdentifier: correlationId,
	};
	launchResource({
		resource,
		workspaceConfiguration,
		resourceContext,
		launchOptions: {
			isAutoLaunch: false,
			chromeAppOptions,
		},
	});
}

function receiveMessageFromChromeApp(
	data: any,
	workspaceConfiguration: WorkspaceConfiguration,
	resourceContext: ResourceContext,
	updateChromeAppContext: ChromeAppContext['updateChromeAppContext']
) {
	if (data.type === 'Settings') {
		postSettingsToChromeApp(workspaceConfiguration);
	} else if (data.type === 'LaunchApp') {
		launchResourceForChromeApp(
			workspaceConfiguration,
			resourceContext,
			data.appID,
			data.uniqueIdentifier
		);
	} else if (data.type === 'Resources') {
		chromeAppRequestResources(workspaceConfiguration.storeProxy.resourcesProxy.listURL);
	} else if (data.type === 'fetchIca') {
		launchResourceForChromeApp(
			workspaceConfiguration,
			resourceContext,
			data.resourceId,
			data.correlationId
		);
	} else if (data.messageType === ListenToChromeAppMessageType.INITIATE_STORE_RESP) {
		const leasingProperties = data?.payload?.leasingProperties?.properties;
		updateChromeAppContext({
			leasingProperties: leasingProperties
				? {
						...getDefaultLeasingProperties(),
						...leasingProperties,
				  }
				: null,
		});
	} else if (data.header?.type === HeaderType.LaunchStatus) {
		handleLaunchStatusFromChromeApp(data.payload, resourceContext);
	} else if (data.header?.type === HeaderType.Troubleshoot) {
		handleTroubleshootMessage(data.payload);
	}
}

export function postICADataErrorToChromeApp(
	errorMsg: string,
	resource: Resource,
	uniqueIdentifier: any
) {
	postMessageToChromeApp({
		type: 'ICAData',
		appID: resource.id,
		uniqueIdentifier,
		error: errorMsg,
	});
}

export function postICADataToChromeApp(
	icaData: any,
	resource: Resource,
	chromeAppOptions: ChromeAppLaunchOptions
) {
	if (chromeAppOptions) {
		postMessageToChromeApp({
			type: 'ICAData',
			appID: resource.id,
			uniqueIdentifier: chromeAppOptions.uniqueIdentifier,
			ICAFile: icaData,
		});
	} else {
		postMessageToChromeApp(icaData);
	}
}

export function postTroubleshootResponse(
	correlationId: string,
	status: 'success' | 'failure',
	errorMessage?: string
) {
	postMessageToChromeApp({
		type: 'troubleshootResp',
		correlationId,
		payload: {
			status,
			errorMessage: errorMessage || null,
		},
	});
}

function postAcknowledgementToChromeApp(messageType: string, correlationId?: string) {
	postMessageToChromeApp({
		type: `${messageType}Ack`,
		correlationId,
	});
}

export function postMessageToChromeApp(data: any) {
	if (chromeAppWindow && chromeAppOrigin) {
		chromeAppWindow.postMessage(data, chromeAppOrigin);
	}
}

function postSettingsToChromeApp(workspaceConfiguration: WorkspaceConfiguration) {
	postMessageToChromeApp({
		type: 'ChromeAppPreferences',
		data: workspaceConfiguration.pluginAssistant.html5.chromeAppPreferences,
	});
}

function chromeAppRequestResources(url: string) {
	const resourcesData = {
		format: 'json',
		resourceDetails: fullDetails,
	};

	post<any>(url, resourcesData)
		.then(data => {
			postResourcesToChromeApp(data.resources);
		})
		.catch(() => {
			postResourcesErrorToChromeApp('ResourcesFailed');
		});
}

export function postResourcesToChromeApp(resources: any) {
	postMessageToChromeApp({ type: 'ResourceData', resources });
}

function postResourcesErrorToChromeApp(error: string) {
	postMessageToChromeApp({ type: 'ResourceData', error });
}

function handleLaunchStatusFromChromeApp(
	payload: LaunchStatusPayload,
	resourceContext: ResourceContext
) {
	const { data, correlationId } = payload || {};
	if (!data?.status || !correlationId) {
		return;
	}
	const { status, resourceId, errorMessage } = data;

	const isSuccess = status === 'success';
	const launchStatus = isSuccess ? LaunchStatus.Completed : LaunchStatus.Failed;
	const launchError = isSuccess ? undefined : errorMessage;

	resourceContext.updateSession(state => {
		const updatedLaunchProgressEntry = {
			...state.launchProgress[correlationId],
			status: launchStatus,
			error: launchError,
			timeStamp: Date.now(),
		};

		const updatedLaunchProgress = {
			...state.launchProgress,
			[correlationId]: updatedLaunchProgressEntry,
		};

		return {
			launchInProgressIds: state.launchInProgressIds.filter(id => id !== resourceId),
			launchProgress: updatedLaunchProgress,
			resourceLatestStatus: getLatestResourceStatus(
				updatedLaunchProgress,
				state.resourceLatestStatus
			),
		};
	});
}

export function isTrusted(config: WorkspaceConfiguration) {
	if (!isCitrixChromeApp()) {
		return true;
	}
	const origins = (config.pluginAssistant.html5.chromeAppOrigins || '').split('|');
	return origins.includes(chromeAppOrigin);
}
